// Fobs ("physical keys") site sync: the flag, the request, and the sync suggestions the API returns
// after configuration changes. The API never starts a Fobs sync on its own; a person always does.
import axios from "axios";
import type { QueryClient } from "@tanstack/react-query";
import { BASE_API_URL } from "store/Me";
import { SITE_SYNC_RECORDS_QUERY_KEY } from "components/AdminSites/siteQueryKeys";
import { siteKeysQueryKey } from "components/AdminSites/siteKeysApi";

/** SiteSyncOperationFlag.PhysicalKeys: sync every fob on a Schlage site to its sister sites. */
export const SYNC_FLAG_PHYSICAL_KEYS = 8;

export type PhysicalKeySyncSuggestionReason =
  | "DefaultFobTypeChanged"
  | "PhysicalKeySharingEnabled"
  | "SisterSiteLinked"
  | "PropertyMigrated";

/** A sharing Schlage site whose existing fobs may need a Fobs sync after a configuration change. */
export interface PhysicalKeySyncSuggestion {
  siteId: string;
  siteName: string | null;
  reason: PhysicalKeySyncSuggestionReason | string;
}

const REASON_LABEL: Record<PhysicalKeySyncSuggestionReason, string> = {
  DefaultFobTypeChanged: "Fob type changed",
  PhysicalKeySharingEnabled: "Fob sharing turned on",
  SisterSiteLinked: "A sister site was linked",
  PropertyMigrated: "Property migrated",
};

export function physicalKeySyncReasonLabel(reason: string): string {
  return REASON_LABEL[reason as PhysicalKeySyncSuggestionReason] ?? reason;
}

/**
 * The suggestions on a save response (site update, status change, site create, property
 * migration), or [] when there are none or the API is older and doesn't send them.
 */
export function physicalKeySyncSuggestionsOf(response: unknown): PhysicalKeySyncSuggestion[] {
  const list = (response as { physicalKeySyncSuggestions?: unknown } | null | undefined)
    ?.physicalKeySyncSuggestions;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (item): item is PhysicalKeySyncSuggestion =>
      !!item && typeof (item as PhysicalKeySyncSuggestion).siteId === "string",
  );
}

/** One entry per site, with every reason given for it. */
export function groupSuggestionsBySite(
  suggestions: readonly PhysicalKeySyncSuggestion[],
): { siteId: string; siteName: string | null; reasons: string[] }[] {
  const bySite = new Map<string, { siteId: string; siteName: string | null; reasons: string[] }>();
  for (const suggestion of suggestions) {
    const entry = bySite.get(suggestion.siteId) ?? {
      siteId: suggestion.siteId,
      siteName: suggestion.siteName,
      reasons: [],
    };
    entry.siteName = entry.siteName || suggestion.siteName;
    if (!entry.reasons.includes(suggestion.reason)) entry.reasons.push(suggestion.reason);
    bySite.set(suggestion.siteId, entry);
  }
  return Array.from(bySite.values());
}

/** Queues a Fobs sync for one site (POST api/Sites/{id}/sync?flags=8). */
export function postFobSync(siteId: string) {
  return axios.post(`${BASE_API_URL}/api/Sites/${siteId}/sync?flags=${SYNC_FLAG_PHYSICAL_KEYS}`);
}

/** Refreshes what a queued Fobs sync changes for a site: its sync records and key views. */
export function invalidateAfterFobSync(queryClient: QueryClient, siteId: string) {
  queryClient.invalidateQueries({ queryKey: [SITE_SYNC_RECORDS_QUERY_KEY, siteId] });
  queryClient.invalidateQueries({ queryKey: siteKeysQueryKey(siteId) });
}
